import React, { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { mapValues, merge } from "lodash-es";

import { IAPIState, IFormButtons, IStatus } from "shared/types";
import { Distance, DistanceUnits, WeightUnits, Weight } from "shared/utils";
import { VENUE_PATHS } from "Venues/router";
import { StageForm } from "Venues/components";
import { IStage, VENUE_ACTIONS } from "Venues/types";
import { dataMetaSelector, editStage } from "Venues/state";
import { useStagePageContext } from "Venues/contexts";
import { addTemporaryToast } from "shared/components/Toast";
import { copy } from "shared/assets";
import { useClearActionMetaOnUnMount, useVenueRoute } from "Venues/utils";

interface EditStageProps {
  updatePlaceholder?: (stage: IStage) => void;
}

export const EditStage: React.FC<EditStageProps> = (props) => {
  const { api: apiMeta } = useSelector(
    dataMetaSelector(VENUE_ACTIONS.editStage)
  );
  useClearActionMetaOnUnMount(VENUE_ACTIONS.editStage);

  const { stageId, stage } = useStagePageContext();
  const { pushRoute } = useVenueRoute();

  const dispatch = useDispatch();

  const buttons: IFormButtons = useMemo(
    () => ({
      primary: {
        "data-testid": "edit_stage_submit",
      },
      secondary: {
        "data-testid": "edit_stage_cancel",
        onClick: () =>
          pushRoute(VENUE_PATHS.STAGE, {
            stageId: stageId || "",
          }),
      },
    }),
    [pushRoute, stageId]
  );

  const defaultValues = useMemo(() => {
    if (stage) {
      const {
        dimensions,
        distanceFromLoadingToStage,
        stageWeightPerSquareMeter,
        ...rest
      } = stage;
      return merge(
        {},
        rest,
        distanceFromLoadingToStage && {
          distanceFromLoadingToStage: new Distance(
            distanceFromLoadingToStage,
            DistanceUnits.mm
          ),
        },
        stageWeightPerSquareMeter && {
          stageWeightPerSquareMeter: new Weight(
            stageWeightPerSquareMeter,
            WeightUnits.g
          ),
        },
        dimensions?.room && {
          dimensions: {
            room: mapValues(
              dimensions.room,
              (val) => new Distance(val, DistanceUnits.mm)
            ),
          },
        },
        dimensions?.stage && {
          dimensions: {
            stage: mapValues(
              dimensions.stage,
              (val) => new Distance(val, DistanceUnits.mm)
            ),
          },
        }
      );
    }
    return {};
  }, [stage]);

  useEffect(() => {
    if (apiMeta?.status === IAPIState.succeeded && stageId) {
      pushRoute(VENUE_PATHS.STAGE, { stageId });
      dispatch(
        addTemporaryToast({
          message: copy.toast.changed,
          status: IStatus.success,
        })
      );
    }
  }, [apiMeta, dispatch, pushRoute, stageId]);

  const handleSubmit = (stage: IStage) => {
    const { stageCapacities, ...stageData } = stage;

    if (stageId) {
      dispatch(editStage({ stageId, stageData }));
    }
  };

  return (
    <StageForm
      key={stageId}
      buttons={buttons}
      hasError={!!apiMeta?.error}
      apiStatus={apiMeta?.status}
      defaultValues={defaultValues}
      handleSubmit={handleSubmit}
      submitMode="isDirty"
      {...props}
    />
  );
};
